import { StateCreator } from "zustand";
import { adjustJobStatus } from "./utils";
import { Logger } from "@/lib/logger/Logger";
import { goBackOrForwardDays } from "../../lib/utils/utils-functions";
import { supabase, Tables } from "@/lib/supabase";
import {
	JobEntityType,
	JobWithShare,
	FileWithShare,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
	SupabaseRpcEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { useCentralStore } from "../Central";
import { showNotification } from "../Central/selectors";
import { produce } from "immer";
import { StorageBucketsEnum, TpTier } from "../../types/enums";
import { usePatientStore } from "../Patient";
import { JobStoreUnion } from ".";
import {
	initialJobDocumentsState,
	JobDocumentWithFiles,
} from "./job-documents.store";
import { initialJobListState, JobStage } from "./job-list.store";
import { initialJobItemState } from "./job-items.store";
import { v4 } from "uuid";
import { uploadFileWithProvidedPath } from "../../hooks/useStorage";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { useRealtimeStore } from "@/lib/store/Realtime";

interface JobState {
	job: JobWithShare | null;
	jobLoading: boolean;
	pendingChanges: boolean;
	labJobFilesLookup: Record<string, FileWithShare>;
	medJobFilesLookup: Record<string, FileWithShare>;
	childJobChannels: Tables<"message_channels">[];
}

export const initialJobState: JobState = {
	job: null,
	jobLoading: false,
	pendingChanges: false,
	labJobFilesLookup: {},
	medJobFilesLookup: {},
	childJobChannels: [],
};

export interface JobSlice extends JobState {
	handleJobRouteChange: (jobIdParam: string) => Promise<void>;
	fetchJob: (jobId: number) => Promise<void>;
	createJob: () => Promise<JobEntityType | null>;
	updateGuarantor: (guarantorId: string | null) => Promise<void>;
	deleteJob: (jobId: number) => Promise<void>; // A jobId is needed because the job can also be deleted from the job list (e.g. archiving a job)
	changeJobStatus: (
		jobId: number, // A jobId is needed because the job can also be changed from the job list (e.g. archiving a job)
		currentStatus: JobStatusEnum,
		targetStatus: JobStatusEnum,
		stateUpdateOnly?: boolean
	) => Promise<boolean>;
	updateJob: (
		field: "title" | "code" | "patient_id" | "is_paid_by_patient",
		value: string | number | boolean,
		overrideJobId?: number
	) => Promise<boolean>;
	reset: () => void;
	setPendingChanges: (pendingChanges: boolean) => void;
	pendingChangesNotification: () => void;
	shareFile: (fileId: string, jobDocumentId?: number) => Promise<void>;
	unshareFile: (fileId: string, jobDocumentId?: number) => Promise<void>;
	uploadFile: (file: File, jobId: number) => Promise<void>;
	fetchJobFiles: (jobId: number) => Promise<void>;
	getDefaultValues: () => Promise<{
		tpValueForJob: number;
		tpTierForJob: TpTier;
		tpVariationForJob: number;
		taxRateForJob: number;
	} | null>;
	acceptJobRequest: (channelId: string | null) => Promise<boolean>;
	denyJobRequest: (channelId: string | null) => Promise<boolean>;
	fetchChildJobChannels: (jobId: number) => Promise<void>;
}

export const createJobStore: StateCreator<JobStoreUnion, [], [], JobSlice> = (
	set,
	get
) => ({
	...initialJobState,
	handleJobRouteChange: async (jobIdParam: string) => {
		const jobId = Number(jobIdParam);

		if (!jobIdParam || jobId === get().job?.id) return;

		const { data, error } = await supabase
			.from("jobs_with_shares")
			.select(
				`*, ${SupabaseTableEnum.JOB_DOCUMENTS} (*,${SupabaseShareEntityViewEnum.FILES_WITH_SHARES}(*))`
			)
			.eq("id", jobId)
			.single();

		if (error) {
			Logger.error(error, {}, "Error fetching job");
			return;
		}

		const { job_documents, ...job } = data;

		const jobDocuments = job_documents.filter(
			(jd: JobDocumentWithFiles) => jd.deleted_at === null
		);

		set({
			job: job,
			jobDocuments: Array.isArray(jobDocuments)
				? jobDocuments.reduce(
					(acc: Record<number, JobDocumentWithFiles>, curr) => {
						acc[curr.id] = curr;
						return acc;
					},
					{}
				)
				: {},
		});

		await Promise.all([
			get().fetchJobFiles(jobId),
			get().fetchChildJobChannels(jobId),
			useRealtimeStore.getState().switchChannelForJobIfExists(job),
		]);
	},
	fetchJobFiles: async (jobId: number) => {
		let organizationId = useCentralStore.getState().organization?.id;

		// If organizationId is not found, central store hasn't finished initializing
		// So get the organizationId from the job
		if (!organizationId) {
			const { data: jobData, error: jobError } = await supabase
				.from(SupabaseTableEnum.JOBS)
				.select("organization_id")
				.eq("id", jobId)
				.single();

			if (jobError) {
				Logger.error(jobError, {}, "Error fetching job");
				return;
			}

			organizationId = jobData?.organization_id;

			if (!organizationId) {
				Logger.error("Organization ID still not found, fetchJobFiles");
				return;
			}
		}

		const { data: jobFiles, error: jobFilesError } = await supabase
			.from(SupabaseShareEntityViewEnum.FILES_WITH_SHARES)
			.select()
			.eq("job_id", jobId);

		if (jobFilesError) {
			showNotification({
				message: "Fehler beim Abrufen der Dateien für den Auftrag",
				type: "error",
			});
			Logger.error(jobFilesError, {}, "Error fetching files for job");
			set({
				labJobFilesLookup: {},
				medJobFilesLookup: {},
			});
			return;
		}

		set(
			produce((state) => {
				state.labJobFilesLookup = jobFiles.reduce(
					(
						acc: Record<string, FileWithShare>,
						row: FileWithShare
					) => {
						if (row.organization_id === organizationId) {
							acc[row.id as string] = row;
						}

						return acc;
					},
					{}
				);
				state.medJobFilesLookup = jobFiles.reduce(
					(
						acc: Record<string, FileWithShare>,
						row: FileWithShare
					) => {
						if (row.organization_id !== organizationId) {
							acc[row.id as string] = row;
						}

						return acc;
					},
					{}
				);
			})
		);
	},

	setPendingChanges: (pendingChanges: boolean) => {
		set({
			pendingChanges,
		});
	},
	createJob: async () => {
		set({
			jobLoading: true,
		});
		const { client, organization } = useCentralStore.getState();

		if (!client || !organization || !organization?.id) {
			showNotification({
				message:
					"Fehler beim Erstellen des Auftrags durch fehlende Daten.",
				type: "error",
			});
			Logger.error(
				"Unexpected error creating job. Missing client or organization data."
			);
			return null;
		}
		const defaultValues = await get().getDefaultValues();

		if (!defaultValues) {
			showNotification({
				message: "Fehler beim Abrufen der Standardwerte für den Auftrag",
				type: "error",
			});
			return null;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.insert({
				code: "", // If code = '' it will be set on insert on the db
				client_id: client.id,
				title: "Neuer Auftrag",
				status: JobStatusEnum.IN_PROGRESS,
				organization_id: organization?.id,
				incoming_order_date: new Date().toISOString(),
				outgoing_order_date: goBackOrForwardDays(7),
				tax_rate: defaultValues.taxRateForJob,
				guarantor_id: client.guarantor_id,
				tp_tier: defaultValues.tpTierForJob,
				tp_value: defaultValues.tpValueForJob,
				tp_variation: defaultValues.tpVariationForJob,
			})
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			Logger.error(error);
			return null;
		}

		if (data) {
			Logger.info({
				message: "Auftrag erstellt",
				type: "success",
			});

			get().addJobToList({
				...data,
				channel_id: null,
				job_document_types: [],
			});

			set({
				jobLoading: false,
			});
			return {
				...data,
				job_documents: [],
				channel_id: null,
			};
		} else {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			return null;
		}
	},
	fetchJob: async (jobId: number) => {
		if (!jobId) return;

		const { data, error } = await supabase
			.from("jobs_with_shares")
			.select(
				`*, ${SupabaseTableEnum.JOB_DOCUMENTS} (*,${SupabaseTableEnum.FILES}(*))`
			)
			.eq("id", jobId)
			.single();

		if (error) {
			set({
				job: null,
			});
			return;
		}

		const { job_documents, ...job } = data;

		set({ job });

		// This is for a case where for some reason the patient hasn't fetched yet; could be done in a more clever way
		if (job.patient_id) {
			await usePatientStore.getState().getPatient(job.patient_id);
		}
		await get().fetchChildJobChannels(jobId);
	},
	deleteJob: async (jobId: number) => {
		let job = get().job;

		// Optimistic update
		get().deleteJobFromList(jobId);
		set(
			produce((state) => {
				// If the currently selected job is deleted, set it to null
				if (state.job?.id === jobId) {
					state.job = null;
				}

				// Remove the job from pinned jobs if it is pinned
				delete state.pinnedJobs[jobId];
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.delete()
			.eq("id", jobId);

		if (error) {
			Logger.error(error, {}, "Error deleting job");
			if (job) get().addJobToList(job);
			set(
				produce((state) => {
					state.job = job;
					state.pinnedJobs[jobId] = job;
				})
			);

			showNotification({
				message: "Fehler beim Löschen des Auftrags",
				type: "error",
			});
			return;
		}
		showNotification({
			message: "Auftrag gelöscht",
			type: "success",
		});
	},

	/**
	 * updateGuarantor
	 * Allowed if: No job document has been created that has job items
	 */
	updateGuarantor: async (guarantorId: string | null) => {
		const { job } = get();
		if (!job) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return;
		}
		set(
			produce((state) => {
				state.job = {
					...state.job,
					guarantor_id: guarantorId,
				};
			})
		);

		const someJobDocumentExported = Object.values(get().jobDocuments).some(
			({ status }) =>
				!(
					status === JobStatusEnum.IN_PROGRESS ||
					status === JobStatusEnum.ARCHIVED
				)
		);
		if (someJobDocumentExported) {
			showNotification({
				message:
					"Der Garant kann nicht mehr geändert werden, da bereits ein Dokument exportiert wurde",
				type: "error",
			});
			return;
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ guarantor_id: guarantorId })
			.eq("id", job.id as number);

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren des Garanten",
				type: "error",
			});
			Logger.log(error);

			// Revert the changes
			set(
				produce((state) => {
					state.job = job;
				})
			);
			return;
		}

		showNotification({
			message: "Garant aktualisiert",
			type: "success",
		});
	},

	changeJobStatus: async (
		jobId: number,
		currentStatus: JobStatusEnum,
		targetStatus: JobStatusEnum,
		stateUpdateOnly: boolean = false
	) => {
		set(
			produce((state) => {
				adjustJobStatus(state, jobId, targetStatus, currentStatus);
				state.job = {
					...state.job,
				};
			})
		);

		if (!stateUpdateOnly) {
			const { error } = await supabase
				.from(SupabaseTableEnum.JOBS)
				.update({
					status: targetStatus,
				})
				.eq("id", jobId);

			if (error) {
				set(
					produce((state) => {
						// Revert the changes
						adjustJobStatus(state, jobId, currentStatus, targetStatus);
					})
				);
				get().fetchJob(jobId);
				return false;
			}
		}

		return true;
	},

	/*
	 * If overrideJobId is not provided, it is set to the current job id
	 * NOT USED TO UPDATE JOB STATUS
	 */
	updateJob: async (
		field: "title" | "code" | "patient_id" | "is_paid_by_patient",
		value: string | number | boolean,
		overrideJobId?: number
	) => {
		let previousJob: JobWithShare | null = null;
		const jobId: number | null = overrideJobId || get().job?.id || null;
		let jobStatus: JobStatusEnum | null = null;
		let jobIndex: number = -1;
		let stageIndex: JobStage | null = null;

		if (!jobId) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return false;
		}

		get().updateJobInList(jobId, field, value);

		set(
			produce((state) => {
				previousJob = state.job;
				jobStatus = state.job?.status as JobStatusEnum;
				state.job[field] = value;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ [field]: value })
			.eq("id", jobId);

		if (error) {
			set(
				produce((state) => {
					state.job = previousJob;
					if (jobIndex !== -1 && jobStatus) {
						if (state.jobList[jobStatus].stages && stageIndex) {
							state.jobList[jobStatus].data[stageIndex].jobs[
								jobIndex
							] = previousJob;
						} else if (!state.jobList[jobStatus].stages) {
							state.jobList[jobStatus].data.jobs[jobIndex] =
								previousJob;
						}
					}
				})
			);
			showNotification({
				message:
					"Fehler beim Aktualisieren des Auftrags in der Datenbank",
				type: "error",
			});
			return false;
		} else {
			showNotification({
				message: "Auftrag aktualisiert",
				type: "success",
			});
		}

		return true;
	},
	reset: () => {
		set({
			...initialJobState,
			...initialJobDocumentsState,
			...initialJobListState,
			...initialJobItemState,
		});
	},
	pendingChangesNotification: () => {
		if (get().pendingChanges) {
			showNotification({
				message:
					"Ausführung trotz ungespeicherter Auftragsinformationen.",
				type: "warning",
			});
		}
	},
	shareFile: async (fileId: string, jobDocumentId?: number) => {
		const { data: shareFileData, error } = await supabase.rpc(SupabaseRpcEnum.SHARE_FILE, {
			p_share_file_id: fileId,
			p_message_content: {
				type: "shared_file",
				file_id: fileId,
			},
		});
		if (error) {
			Logger.error("Error sharing file", {}, error);
			showNotification({
				message: "Fehler beim Teilen der Datei",
				type: "error",
			});
		} else if (!(shareFileData as any)?.success) {
			showNotification({
				message: (shareFileData as any)?.message,
				type: "error",
			});
		} else {
			showNotification({
				message: (shareFileData as any)?.message,
				type: "success",
			});

			// Update file to display it as shared
			if (jobDocumentId) {
				// TODO: properly update only the file in question
				// refetch the job document
				get().fetchJobDocument(jobDocumentId);
			} else {
				// TODO: properly update only the file in question
				// for now just refetch the job
				await get().fetchJobFiles(get().job?.id ?? 0);
				const { data: jobData, error: jobError } = await supabase
					.from(SupabaseTableEnum.FILES)
					.select("*")
					.eq("id", fileId);

				if (jobData && jobData[0].job_document_id) {
					get().fetchJobDocument(jobData[0].job_document_id);
				}
			}

			// If job wasn't shared yet, update state
			if (!get().job?.shared_with) {
				set(
					produce((state) => {
						state.job["shared_with"] = v4();
					})
				);
			}
		}

		Logger.log("Shared file", {}, { shareFileData });

		// If the channel was newly created (file share was first message), switch to it
		if ((shareFileData as any)?.channel_created && (shareFileData as any).channel_id) {
			useRealtimeStore.getState().changeChannel((shareFileData as any).channel_id as string);
		}
	},
	unshareFile: async (fileId: string, jobDocumentId?: number) => {
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.UNSHARE_FILE,
			{
				unshare_file_id: fileId,
			}
		);

		if (error) {
			showNotification({
				message: "Fehler beim Aufheben der Dateifreigabe",
				type: "error",
			});
		} else if (!(data as any)?.success) {
			showNotification({
				message: (data as any)?.message,
				type: "error",
			});
		} else {
			showNotification({
				message: "Datei nicht mehr geteilt",
				type: "success",
			});

			// Update file to display it as shared
			if (jobDocumentId) {
				// TODO: properly update only the file in question
				// refetch the job document
				get().fetchJobDocument(jobDocumentId);
			} else {
				// TODO: properly update only the file in question
				// for now just refetch the job
				await get().fetchJobFiles(get().job?.id ?? 0);
				const { data: jobData, error: jobError } = await supabase
					.from(SupabaseTableEnum.FILES)
					.select("*")
					.eq("id", fileId);
				if (jobData && jobData[0].job_document_id) {
					get().fetchJobDocument(jobData[0].job_document_id);
				}
			}

			// Update job to display it as not shared
			set(
				produce((state) => {
					// Update the specific file in the lookup
					if (state.jobFilesLookup[fileId]) {
						state.jobFilesLookup[fileId].shared_until = new Date();
					}
				})
			);
		}
	},

	uploadFile: async (file: File, jobId: number) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Keine Organisation ausgewählt)",
				type: "error",
			});
			return;
		}

		const fileName = file.name;
		const filePath = `${v4()}/${fileName}`;

		const { success, pathName } = await uploadFileWithProvidedPath({
			fileBody: file,
			filePath: filePath,
		});

		if (!success || !pathName) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Fehler beim Hochladen der Datei auf den Server)",
				type: "error",
			});
			Logger.error("Error uploading file for job");
			return;
		}
		const { error, data } = await supabase
			.from(SupabaseTableEnum.FILES)
			.insert([
				{
					file_name: fileName,
					bucket_name: StorageBucketsEnum.V1,
					path_name: pathName,
					job_id: jobId,
					organization_id: organizationId,
					meta_data: createFileMetadata(file),
				},
			])
			.select()
			.single();

		if (error) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Fehler beim Speichern der Datei in der Datenbank)",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.labJobFilesLookup[data.id] = data;
			})
		);
	},
	getDefaultValues: async () => {
		const { client, organization } = useCentralStore.getState();
		if (!client?.id || !organization?.id) {
			showNotification({
				message: "Fehler beim Abrufen der Standardwerte für den Auftrag",
				type: "error",
			});
			return null;
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.GET_JOB_DEFAULT_VALUES,
			{
				p_client_id: client.id,
				p_organization_id: organization.id,
			}
		);

		if (error || !(data as any)?.success) {
			showNotification({
				message: "Fehler beim Abrufen der Standardwerte für den Auftrag",
				type: "error",
				description: error ? error.message : (data as any)?.message,
			});
			return null;
		}

		return {
			tpValueForJob: (data as any)?.tp_value as number,
			tpTierForJob: (data as any)?.tp_tier as TpTier,
			tpVariationForJob: (data as any)?.tp_variation as number,
			taxRateForJob: (data as any)?.tax_rate as number,
		};
	},
	acceptJobRequest: async (channelId: string | null) => {
		if (!channelId) {
			showNotification({
				message: "Fehler beim Annehmen des Auftrags",
				type: "error",
			});
			return false;
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.ACCEPT_JOB_REQUEST,
			{
				p_channel_id: channelId,
			}
		);

		if (error) {
			showNotification({
				message: "Fehler beim Annehmen des Auftrags",
				type: "error",
				description: error.message,
			});
			Logger.error(error, {}, "Error accepting job request");
			return false;
		}

		if (data && !(data as any)?.success) {
			showNotification({
				message: (data as any)?.message || "Fehler beim Annehmen des Auftrags",
				type: "error",
			});
			return false;
		}

		// The RPC has created a new job
		const newJob = (data as any)?.job;
		if (newJob) {
			set({
				job: newJob,
			});

			// TODO: This is a hack to refresh the channels, but it's not the best solution
			// because it fetches all channels, not just the one that is relevant to the job
			useRealtimeStore.getState().fetchLatestChannels();

			get().addJobToList({
				...newJob,
				channel_id: channelId,
				job_document_types: [],
			});
		}

		showNotification({
			message: "Auftrag erfolgreich angenommen",
			type: "success",
		});

		return true;
	},
	denyJobRequest: async (channelId: string | null) => {
		if (!channelId) {
			showNotification({
				message: "Fehler beim Ablehnen des Auftrags",
				type: "error",
			});
			return false;
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.DENY_JOB_REQUEST,
			{
				p_channel_id: channelId,
			}
		);

		if (error || !(data as any)?.success) {
			showNotification({
				message: "Fehler beim Ablehnen des Auftrags",
				type: "error",
				description: error ? error.message : (data as any)?.message,
			});
			Logger.error(error, {}, "Error denying job request");
			return false;
		}

		// We don't show a success message here because a message will be sent in the opened channel

		return true;
	},
	fetchChildJobChannels: async (jobId: number) => {
		const { data, error } = await supabase
			.from("message_channels")
			.select("*")
			.eq("parent_job_id", jobId);

		if (error) {
			Logger.error(error, {}, "Error fetching child jobs");
			showNotification({
				message: "Fehler beim Abrufen der Aufträge",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.childJobChannels = data;
			})
		);
	},
});
